'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useAnalytics } from '@/hooks/useAnalytics';

// Navigation items
const navItems = [
  {
    name: 'Features',
    href: '/features',
    children: [
      { name: 'Communication', href: '/features/communication' },
      { name: 'Train', href: '/features/train' },
      { name: 'Compete', href: '/features/compete' },
    ],
  },
  { name: 'Pricing', href: '/pricing' },
  { name: 'About', href: '/about' },
  { name: 'Blog', href: '/blog' },
  { name: 'Contact', href: '/contact' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const pathname = usePathname();
  const { trackClick } = useAnalytics();
  const headerRef = useRef<HTMLElement>(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle dropdown toggle
  const toggleDropdown = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    trackClick(mobileMenuOpen ? 'mobile_menu_close' : 'mobile_menu_open', 'header');
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <header
      ref={headerRef}
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-futuristic-black/80 backdrop-blur-md border-b border-white/5 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-2 group">
            <motion.span 
              className="text-2xl font-black italic font-kanit relative"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              SKETCHPLAY
              <motion.span 
                className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-futuristic-neon to-futuristic-blue group-hover:w-full transition-all duration-300"
                layoutId="underline"
              />
            </motion.span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-10">
            {navItems.map((item) => (
              <div key={item.name} className="relative group">
                {item.children ? (
                  <button
                    className={`flex items-center text-sm font-medium ${
                      pathname?.startsWith(item.href)
                        ? 'text-white'
                        : 'text-gray-300 hover:text-white'
                    } transition-colors`}
                    onClick={() => toggleDropdown(item.name)}
                    onMouseEnter={() => setActiveDropdown(item.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {item.name}
                    <motion.svg
                      className="ml-1 h-4 w-4"
                      animate={{ rotate: activeDropdown === item.name ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </motion.svg>
                  </button>
                ) : (
                  <Link
                    href={item.href}
                    className="relative text-sm font-medium text-gray-300 hover:text-white transition-colors duration-300 group"
                  >
                    {item.name}
                    <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-futuristic-neon group-hover:w-full transition-all duration-300 ${
                      pathname === item.href ? 'w-full' : 'w-0'
                    }`} />
                  </Link>
                )}

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {item.children && activeDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 mt-2 w-48 rounded-md bg-futuristic-black/90 backdrop-blur-md border border-white/10 shadow-blue-glow overflow-hidden"
                      onMouseEnter={() => setActiveDropdown(item.name)}
                      onMouseLeave={() => setActiveDropdown(null)}
                    >
                      <div className="py-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            href={child.href}
                            className={`block px-4 py-2 text-sm ${
                              pathname === child.href
                                ? 'text-futuristic-neon bg-white/5'
                                : 'text-gray-300 hover:bg-white/5 hover:text-white'
                            } transition-colors duration-200`}
                            onClick={() => {
                              setActiveDropdown(null);
                              trackClick(`nav_${child.name.toLowerCase()}`, 'header_dropdown');
                            }}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="minimal"
              size="sm"
              href="https://web.sketchplay.io/"
              target="_blank"
              rel="noopener noreferrer"
              trackingName="login_button"
              trackingLocation="header"
              className="text-white hover:text-futuristic-neon transition-colors duration-300"
            >
              Log in
            </Button>
            <Button
              variant="neon"
              size="sm"
              href="/demo"
              trackingName="demo_button"
              trackingLocation="header"
              className="rounded-full px-6"
            >
              Get a Demo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden rounded-full p-2 text-gray-300 hover:text-white border border-white/10 hover:border-white/20 transition-colors duration-300"
            onClick={toggleMobileMenu}
            whileTap={{ scale: 0.95 }}
          >
            <span className="sr-only">Open main menu</span>
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <motion.span
                className="w-5 h-0.5 bg-current block transition-all duration-300"
                animate={{ 
                  rotate: mobileMenuOpen ? 45 : 0,
                  y: mobileMenuOpen ? 0 : -4
                }}
              />
              <motion.span
                className="w-5 h-0.5 bg-current block my-1 transition-all duration-300"
                animate={{ 
                  opacity: mobileMenuOpen ? 0 : 1,
                  x: mobileMenuOpen ? -20 : 0
                }}
              />
              <motion.span
                className="w-5 h-0.5 bg-current block transition-all duration-300"
                animate={{ 
                  rotate: mobileMenuOpen ? -45 : 0,
                  y: mobileMenuOpen ? 0 : 4
                }}
              />
            </div>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="md:hidden fixed inset-0 z-40 mt-16 bg-futuristic-black/95 backdrop-blur-md"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-4 pt-8 pb-6 space-y-6 flex flex-col h-full">
              <div className="space-y-6 flex-1">
                {navItems.map((item) => (
                  <div key={item.name} className="space-y-2">
                    {item.children ? (
                      <>
                        <button
                          className={`w-full flex justify-between items-center py-2 text-lg font-medium ${
                            pathname?.startsWith(item.href)
                              ? 'text-futuristic-neon'
                              : 'text-white'
                          } border-b border-white/10 pb-2`}
                          onClick={() => toggleDropdown(item.name)}
                        >
                          {item.name}
                          <motion.svg
                            className="h-5 w-5"
                            animate={{ rotate: activeDropdown === item.name ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </motion.svg>
                        </button>
                        <AnimatePresence>
                          {activeDropdown === item.name && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="pl-4 space-y-2 border-l border-white/10 ml-2"
                            >
                              {item.children.map((child) => (
                                <Link
                                  key={child.name}
                                  href={child.href}
                                  className={`block py-2 text-base ${
                                    pathname === child.href
                                      ? 'text-futuristic-neon'
                                      : 'text-gray-300 hover:text-white'
                                  } transition-colors`}
                                  onClick={() => {
                                    setActiveDropdown(null);
                                    trackClick(`nav_${child.name.toLowerCase()}`, 'header_mobile');
                                  }}
                                >
                                  {child.name}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        href={item.href}
                        className={`block py-2 text-lg font-medium ${
                          pathname === item.href
                            ? 'text-futuristic-neon'
                            : 'text-white'
                        } border-b border-white/10 pb-2 transition-colors`}
                        onClick={() => trackClick(`nav_${item.name.toLowerCase()}`, 'header_mobile')}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
              <div className="pt-6 border-t border-white/10 space-y-4">
                <Button
                  variant="minimal"
                  size="lg"
                  href="https://web.sketchplay.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  trackingName="login_button_mobile"
                  trackingLocation="header_mobile"
                  className="w-full justify-center"
                >
                  Log in
                </Button>
                <Button
                  variant="neon"
                  size="lg"
                  href="/demo"
                  trackingName="demo_button_mobile"
                  trackingLocation="header_mobile"
                  className="w-full justify-center rounded-full"
                >
                  Get a Demo
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
} 